import type { FileProcessResultResponse } from '@/api/models/FileProcessResultResponse';
import { Button, Icon, TextLink } from '@lego/connect-components-react';
import { Flex } from '@lego/klik-ui';
import type { ReactNode } from 'react';

type UploadFinishedViewProps = {
  uploadResult: FileProcessResultResponse | undefined;
  errorMessage?: string | null;
  successMessage: ReactNode;
  onRetry: () => void;
};

const UploadFinishedView = ({
  uploadResult,
  errorMessage,
  successMessage,
  onRetry,
}: UploadFinishedViewProps) => {
  const isSuccess = uploadResult?.status === 'Completed';
  const isParsingError = uploadResult?.status === 'ParsingValidationErrors';
  const isCalculationError = uploadResult?.status === 'CalculationErrors';
  const hasErrorReport = isParsingError || isCalculationError;

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="16px"
      flex="1"
      width="100%"
    >
      {isSuccess ? (
        <Icon
          name="status-circle-check-solid"
          aria-label="success"
          size="x-large"
          color="ds-color-interactive-positive-enabled"
        />
      ) : (
        <Icon
          name="status-circle-exclamation-solid"
          aria-label="error"
          size="x-large"
          color="ds-color-support-negative-default"
        />
      )}

      <span className="label-lg-medium">
        {isSuccess ? 'Upload complete' : 'Upload failed'}
      </span>

      {isSuccess ? (
        <span className="body-md-regular" style={{ textAlign: 'center' }}>
          {successMessage}
        </span>
      ) : (
        <Flex
          flexDirection="column"
          alignItems="center"
          gap="8px"
          textAlign="center"
        >
          <span className="body-md-regular">
            {isParsingError
              ? 'There were validation errors during parsing.'
              : isCalculationError
                ? 'There were errors processing prices.'
                : errorMessage
                  ? `An error occurred. ${errorMessage}`
                  : 'An unexpected error occurred during the upload process. Please try uploading the file again.'}
          </span>
          {hasErrorReport && uploadResult?.errorReportUrl && (
            <TextLink href={uploadResult.errorReportUrl} size="medium">
              Download error details
            </TextLink>
          )}
        </Flex>
      )}

      {!isSuccess && (
        <Button
          size="small"
          appearance="outline"
          variant="primary"
          onClick={onRetry}
        >
          Try again
        </Button>
      )}
    </Flex>
  );
};

export default UploadFinishedView;
